import React, {
	useState,
	useCallback,
	useEffect,
	useContext,
	useRef
} from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import {
	searchV2FilterOptions,
	FILTER_GROUPS,
	getFilterGroupKey
} from "config/filters";
import SearchPageContext from "pages/SearchPageV1/SearchPageV1Context";
import FiltersHeader from "./FiltersHeader";
import FilterSection from "./FilterSection";
import "./FiltersCard.scss";

const buildItemsFromFilterGroup = (groupKey) => {
	const group = searchV2FilterOptions[groupKey];

	if (!group) return [];

	return Object.values(group.options).map((option, index) => ({
		id: `${groupKey}-${index + 1}`,
		label: option.label,
		name: option.name,
		value: option.value,
		checked: option.checked || false
	}));
};

const buildSectionFromFilterGroup = (groupKey, title) => {
	const group = searchV2FilterOptions[groupKey];

	return {
		id: groupKey,
		groupKey,
		title,
		multiple: group ? group.multiple : true,
		items: buildItemsFromFilterGroup(groupKey)
	};
};

const INITIAL_SECTIONS = [
	buildSectionFromFilterGroup(FILTER_GROUPS.AMENITIES, "Amenities"),
	buildSectionFromFilterGroup(FILTER_GROUPS.UNIT_SIZE, "Size"),
	buildSectionFromFilterGroup(FILTER_GROUPS.VEHICLE_STORAGE, "Storage Type"),
	buildSectionFromFilterGroup(FILTER_GROUPS.VEHICLE_SIZE, "Size")
];

const buildSectionsFromActiveFilters = (filters) => {
	if (!filters?.length) return INITIAL_SECTIONS;

	const mapped = filters
		.map((group, groupIndex) => {
			if (!group) return null;

			let title = group.label || `Group ${groupIndex + 1}`;
			if (group.label?.includes("Amenities")) {
				title = "Amenities";
			} else if (group.label?.includes("Size Filters")) {
				title = "Size";
			} else if (group.label?.includes("Vehicle Storage")) {
				title = "Storage Type";
			} else if (group.label?.includes("Size")) {
				title = "Size";
			}

			const options = Object.values(group.options || {});
			const groupKey = getFilterGroupKey(group);
			// Keyed off groupKey, not title: sqft and vehicleSize both display as "Size" and would collide.
			const idPrefix = groupKey || title;

			return {
				id: `${idPrefix}-${groupIndex}`,
				groupKey,
				title,
				multiple: group.multiple,
				items: options.map((option, index) => ({
					id: `${idPrefix}-${index + 1}`,
					label: option.label,
					name: option.name,
					value: option.value,
					checked: !!option.checked
				}))
			};
		})
		.filter(Boolean);

	return mapped;
};

const TABS = {
	SELF_STORAGE: "self-storage",
	VEHICLE_STORAGE: "vehicle-storage"
};

const TAB_ORDER = [TABS.SELF_STORAGE, TABS.VEHICLE_STORAGE];

const TAB_IDS = {
	[TABS.SELF_STORAGE]: "filters-tab-self-storage",
	[TABS.VEHICLE_STORAGE]: "filters-tab-vehicle-storage"
};

const PANEL_IDS = {
	[TABS.SELF_STORAGE]: "filters-panel-self-storage",
	[TABS.VEHICLE_STORAGE]: "filters-panel-vehicle-storage"
};

const FiltersCard = ({
	onChange = undefined,
	onFilterChange = undefined,
	navigateOnChange = false,
	activeFilters = undefined,
	onClose = undefined,
	onApply = undefined
}) => {
	const [sections, setSections] = useState(() =>
		buildSectionsFromActiveFilters(activeFilters)
	);
	const [hasPendingClear, setHasPendingClear] = useState(false);
	const [activeTab, setActiveTab] = useState(TABS.SELF_STORAGE);
	const tabRefs = useRef({});

	const handleTabKeyDown = (event) => {
		if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
		event.preventDefault();
		const currentIndex = TAB_ORDER.indexOf(activeTab);
		const delta = event.key === "ArrowRight" ? 1 : -1;
		const nextTab =
			TAB_ORDER[
				(currentIndex + delta + TAB_ORDER.length) % TAB_ORDER.length
			];
		setActiveTab(nextTab);
		tabRefs.current[nextTab]?.focus();
	};
	const [collapsedSections, setCollapsedSections] = useState(() => {
		if (!onApply) return {};
		// Each tab's first section starts expanded, as does any section with a selection already checked.
		const expandedGroups = [
			FILTER_GROUPS.AMENITIES,
			FILTER_GROUPS.VEHICLE_STORAGE
		];
		return sections.reduce((acc, s) => {
			const hasSelectedItem = s.items.some((item) => item.checked);
			return {
				...acc,
				[s.id]: !expandedGroups.includes(s.groupKey) && !hasSelectedItem
			};
		}, {});
	});
	const { setIsFiltering } = useContext(SearchPageContext) || {};

	const handleToggleSection = useCallback((sectionId) => {
		setCollapsedSections((prev) => ({
			...prev,
			[sectionId]: !prev[sectionId]
		}));
	}, []);

	const handleNavigateOnChangeEffects = (shouldNavigate) => {
		if (!shouldNavigate) return;
		if (setIsFiltering) {
			setIsFiltering(true);
		}
		if (onClose) {
			onClose();
		}
	};

	// Discard the uncommitted draft (sections, pending clear) when closed via
	// this handler, i.e. the header's X button. Current consumers unmount
	// FiltersCard on close instead, which clears all local state anyway; this
	// only matters if a future consumer keeps it mounted and hides it via CSS
	// — and even then, only for closes that go through this handler.
	const handleClose = () => {
		if (onApply) {
			setSections(buildSectionsFromActiveFilters(activeFilters));
			setHasPendingClear(false);
		}
		if (onClose) {
			onClose();
		}
	};

	// Keep local checkbox state in sync with Redux / URL-driven
	// filters whenever activeFilters changes.
	useEffect(() => {
		const nextSections = buildSectionsFromActiveFilters(activeFilters);
		setSections(nextSections);
	}, [activeFilters]);

	// Expand (never re-collapse) a section when it gains a checked filter.
	// Gated on the section transitioning from unchecked -> checked, not just
	// "currently has a checked item" — otherwise toggling any other filter
	// re-runs this effect and force-reopens a section the user had manually
	// collapsed while it still held a checked item.
	const prevHasSelectedRef = useRef({});
	useEffect(() => {
		// Snapshot before the ref is overwritten below — setCollapsedSections's
		// updater runs later (during React's commit), by which point the ref
		// mutation at the end of this effect would already have landed,
		// making "previously had a selection" read as the *new* state.
		const previousHasSelected = prevHasSelectedRef.current;
		if (onApply) {
			setCollapsedSections((prev) => {
				let changed = false;
				const next = { ...prev };
				sections.forEach((s) => {
					const hasSelectedItem = s.items.some(
						(item) => item.checked
					);
					const previouslyHadSelectedItem = previousHasSelected[s.id];
					if (
						hasSelectedItem &&
						!previouslyHadSelectedItem &&
						next[s.id]
					) {
						next[s.id] = false;
						changed = true;
					}
				});
				return changed ? next : prev;
			});
		}
		prevHasSelectedRef.current = sections.reduce((acc, s) => {
			acc[s.id] = s.items.some((item) => item.checked);
			return acc;
		}, {});
	}, [sections, onApply]);

	const emitChange = useCallback(
		(updatedSections) => {
			if (onChange) {
				onChange(updatedSections);
			}
		},
		[onChange]
	);

	// What's actually applied in Redux, so the local draft can be diffed against it to detect changes.
	const appliedChecked = {};
	if (activeFilters?.length) {
		activeFilters.forEach((group) => {
			Object.values(group.options || {}).forEach((option) => {
				appliedChecked[`${option.name}::${option.value}`] =
					!!option.checked;
			});
		});
	}

	const handleClear = () => {
		const previouslyCheckedItems = [];
		if (activeFilters?.length) {
			activeFilters.forEach((group) => {
				Object.values(group.options).forEach((option) => {
					if (option.checked) {
						previouslyCheckedItems.push({
							name: option.name,
							value: option.value
						});
					}
				});
			});
		} else {
			sections.forEach((section) => {
				section.items.forEach((item) => {
					if (item.checked) {
						previouslyCheckedItems.push({
							name: item.name,
							value: item.value
						});
					}
				});
			});
		}

		const cleared = sections.map((section) => ({
			...section,
			items: section.items.map((item) => ({
				...item,
				checked: false
			}))
		}));
		setSections(cleared);
		emitChange(cleared);

		if (onApply) {
			setHasPendingClear(true);
			return;
		}

		if (onFilterChange && previouslyCheckedItems.length > 0) {
			handleNavigateOnChangeEffects(navigateOnChange);
			previouslyCheckedItems.forEach((item, index) => {
				const isLast = index === previouslyCheckedItems.length - 1;
				const syntheticEvent = {
					target: {
						name: item.name,
						value: item.value,
						checked: false
					}
				};
				onFilterChange(syntheticEvent, navigateOnChange && isLast);
			});
		}
	};

	const handleApply = () => {
		if (onFilterChange) {
			sections.forEach((section) => {
				section.items.forEach((item) => {
					const key = `${item.name}::${item.value}`;
					const wasChecked = !!appliedChecked[key];
					if (item.checked !== wasChecked) {
						onFilterChange(
							{
								target: {
									name: item.name,
									value: item.value,
									checked: item.checked
								}
							},
							false
						);
					}
				});
			});
		}
		setHasPendingClear(false);
		onApply();
	};

	const handleItemChange = (sectionId, itemId) => {
		const updated = sections.map((section) => {
			if (section.id !== sectionId) return section;
			const isSingleSelect = section.multiple === false;
			return {
				...section,
				items: section.items.map((item) => {
					if (item.id === itemId) {
						return { ...item, checked: !item.checked };
					}
					if (isSingleSelect) {
						return { ...item, checked: false };
					}
					return item;
				})
			};
		});
		setSections(updated);
		emitChange(updated);

		if (onApply) return;

		if (onFilterChange) {
			const section = updated.find((s) => s.id === sectionId);
			if (section) {
				const toggledItem = section.items.find(
					(item) => item.id === itemId
				);
				if (toggledItem) {
					const syntheticEvent = {
						target: {
							name: toggledItem.name,
							value: toggledItem.value,
							checked: toggledItem.checked
						}
					};
					handleNavigateOnChangeEffects(navigateOnChange);
					onFilterChange(syntheticEvent, navigateOnChange);
				}
			}
		}
	};

	// Driven off the local draft so it responds before the selection is committed to Redux.
	const hasCheckedFilter = sections.some((section) =>
		section.items.some((item) => item.checked)
	);
	const isClearDisabled = !hasCheckedFilter;

	// Also true when a checked filter got unchecked, even though nothing ends up checked.
	const hasChanges = sections.some((section) =>
		section.items.some(
			(item) =>
				item.checked !== !!appliedChecked[`${item.name}::${item.value}`]
		)
	);
	const hasActiveFilters = hasChanges || hasPendingClear;

	// Looked up by group key, not position — the section list can drop empty groups.
	const findSection = (groupKey) =>
		sections.find((s) => s.groupKey === groupKey);

	const amenitiesSection = findSection(FILTER_GROUPS.AMENITIES);
	const sizeSection = findSection(FILTER_GROUPS.UNIT_SIZE);
	const vehicleStorageSection = findSection(FILTER_GROUPS.VEHICLE_STORAGE);
	const vehicleSizeSection = findSection(FILTER_GROUPS.VEHICLE_SIZE);

	const selfStorageSizeChecked = sizeSection?.items.some((i) => i.checked);
	const vehicleStorageChecked =
		vehicleStorageSection?.items.some((i) => i.checked) ||
		vehicleSizeSection?.items.some((i) => i.checked);

	const renderFilterSection = (section, extraProps = {}) =>
		section && (
			<FilterSection
				id={section.id}
				title={section.title}
				items={section.items}
				onItemChange={handleItemChange}
				collapsed={!!collapsedSections[section.id]}
				onToggle={() => handleToggleSection(section.id)}
				activeCount={section.items.filter((i) => i.checked).length}
				{...extraProps}
			/>
		);

	return (
		<section
			className="filters"
			aria-label="Filters"
		>
			<FiltersHeader
				title="Filters"
				onClear={handleClear}
				onClose={onClose && handleClose}
				isClearDisabled={isClearDisabled}
			/>
			<div className="filters__content">
				<div
					className="filters__tabs"
					role="tablist"
					// Satisfies jsx-a11y/interactive-supports-focus for role="tablist";
					// -1 keeps it out of the tab order since the individual tab
					// buttons below own the roving tabindex (0 on the active tab).
					tabIndex={-1}
					onKeyDown={handleTabKeyDown}
				>
					<button
						type="button"
						id={TAB_IDS[TABS.SELF_STORAGE]}
						ref={(el) => {
							tabRefs.current[TABS.SELF_STORAGE] = el;
						}}
						role="tab"
						aria-selected={activeTab === TABS.SELF_STORAGE}
						aria-controls={PANEL_IDS[TABS.SELF_STORAGE]}
						tabIndex={activeTab === TABS.SELF_STORAGE ? 0 : -1}
						className={`filters__tab${activeTab === TABS.SELF_STORAGE ? " filters__tab--active" : ""}`}
						onClick={() => setActiveTab(TABS.SELF_STORAGE)}
					>
						Self Storage
					</button>
					<button
						type="button"
						id={TAB_IDS[TABS.VEHICLE_STORAGE]}
						ref={(el) => {
							tabRefs.current[TABS.VEHICLE_STORAGE] = el;
						}}
						role="tab"
						aria-selected={activeTab === TABS.VEHICLE_STORAGE}
						aria-controls={PANEL_IDS[TABS.VEHICLE_STORAGE]}
						tabIndex={activeTab === TABS.VEHICLE_STORAGE ? 0 : -1}
						className={`filters__tab${activeTab === TABS.VEHICLE_STORAGE ? " filters__tab--active" : ""}`}
						onClick={() => setActiveTab(TABS.VEHICLE_STORAGE)}
					>
						Vehicle Storage
					</button>
				</div>

				{activeTab === TABS.SELF_STORAGE && (
					<div
						className="filters__columns"
						role="tabpanel"
						id={PANEL_IDS[TABS.SELF_STORAGE]}
						aria-labelledby={TAB_IDS[TABS.SELF_STORAGE]}
					>
						<div className="filters__left-col">
							{renderFilterSection(amenitiesSection)}
						</div>
						<div className="filters__separator" />
						<div className="filters__right-col">
							{renderFilterSection(sizeSection, {
								disabled: vehicleStorageChecked
							})}
						</div>
					</div>
				)}

				{activeTab === TABS.VEHICLE_STORAGE && (
					<div
						className="filters__columns"
						role="tabpanel"
						id={PANEL_IDS[TABS.VEHICLE_STORAGE]}
						aria-labelledby={TAB_IDS[TABS.VEHICLE_STORAGE]}
					>
						<div className="filters__left-col">
							{renderFilterSection(vehicleStorageSection, {
								inputType: "radio",
								disabled: selfStorageSizeChecked
							})}
						</div>
						<div className="filters__separator" />
						<div className="filters__right-col">
							{renderFilterSection(vehicleSizeSection, {
								inputType: "radio",
								disabled: selfStorageSizeChecked
							})}
						</div>
					</div>
				)}
			</div>
			{onApply && (
				<button
					type="button"
					className={classNames("filters__apply-button", {
						"filters__apply-button--active": hasActiveFilters
					})}
					onClick={handleApply}
					disabled={!hasActiveFilters}
				>
					Apply Filters
				</button>
			)}
		</section>
	);
};

FiltersCard.propTypes = {
	onChange: PropTypes.func,
	onFilterChange: PropTypes.func,
	navigateOnChange: PropTypes.bool,
	activeFilters: PropTypes.arrayOf(PropTypes.object),
	onClose: PropTypes.func,
	onApply: PropTypes.func
};

export default FiltersCard;
